import { Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { LaunchDarklyService } from 'src/app/shared/services/launch-darkly.service';
import { ClientSideValidator, DeploymentTypeMetadata, InputType, UserInputPropertySource } from '../models/deploymentForm.model';

/**
 * This service provides helper methods throughout deployment form components to help create, bind, and manage 
 * dynamic content driven by metadata.
 */
@Injectable({
  providedIn: 'root'
})
export class SystemFormService {
  constructor(private readonly ldService: LaunchDarklyService) { }

  /**
   * Returns true if the given metadata item should be visible based on its LaunchDarkly feature flag.
   * When no featureFlag is set, the item is always visible (existing logic applies).
   * When a featureFlag is set, it must evaluate to true for the item to be visible.
   */
  isFeatureEnabled(metadata: DeploymentTypeMetadata): boolean {
    if (!metadata?.featureFlag) {
      return true; // No flag gate — use existing visibility logic
    }
    return this.ldService.getBooleanFlag(metadata.featureFlag, false);
  }

  /**
   * Creates a new form group with default values dynamically from metadata.
   * @param formMetadata Metadata for the form.
   * @returns Angular FormGroup constructed from metadata with default values.
   */
  getSystemForm(formMetadata: DeploymentTypeMetadata): FormGroup {
    let systemForm = new UntypedFormGroup({
      name: new FormControl(formMetadata.name, Validators.required)
    });

    formMetadata.requiredParameters.forEach(param => systemForm.addControl(param.name, this.createDynamicFormControl(param, true)));
    formMetadata.optionalParameters.forEach(param => systemForm.addControl(param.name, this.createDynamicFormControl(param, false)));

    systemForm.addControl('optionalFeatures', new FormArray([]));

    if (formMetadata.optionalFeatures && formMetadata.optionalFeatures?.length > 0) {
      let optionalFeaturesFormArray: FormArray = systemForm.get('optionalFeatures') as FormArray;

      formMetadata.optionalFeatures.forEach(optionalFeature => {
        if (this.isDeploymentScopedFeature(formMetadata, optionalFeature.name)) {
          return;
        }

        if (optionalFeature.enableByDefault && this.isFeatureEnabled(optionalFeature)) {
          let optionalFeatureMetadata = formMetadata.optionalFeatures.find(feature => feature.name === optionalFeature.name);
          optionalFeaturesFormArray.push(this.getSystemForm(optionalFeatureMetadata));
        }
      });
    }

    return systemForm;
  }

  /**
   * Takes an existing system model for a provisioned device and binds it to a form group created by the original metadata. 
   * @param formMetadata Metadata for the form.
   * @param formModel Model with existing data.
   * @returns Angular FormGroup constructed from metadata and patched with existing data.
   */
  bindSystemForm(formMetadata: DeploymentTypeMetadata, formModel: any): FormGroup {
    // Defensive guards & diagnostics
    if (!formMetadata) {
      console.error('[SystemFormService] bindSystemForm called with undefined formMetadata. formModel:', formModel);
      // Return a minimal form so caller logic can continue without blowing up.
      return new UntypedFormGroup({ name: new FormControl(formModel?.name || '') });
    }
    if (!formModel) {
      console.warn(`[SystemFormService] bindSystemForm called with null/undefined formModel for metadata '${formMetadata.name}'. Using empty defaults.`);
      return this.getSystemForm(formMetadata);
    }
    if (formMetadata.name !== formModel.name) {
      console.warn(`Binding warning - metadata key '${formMetadata.name}' did not match system form key '${formModel.name}'.`);
    }

    let systemForm = this.getSystemForm(formMetadata);
    console.debug('[SystemFormService] Binding system form', {
      metadataName: formMetadata.name,
      modelName: formModel.name,
      hasOptionalFeatures: Array.isArray(formModel.optionalFeatures) && formModel.optionalFeatures.length > 0
    });

    // This type conversion will eventually be handled on the server, and can then be removed
    formMetadata.requiredParameters.forEach(requiredParam => {
      if (requiredParam.userInputType === InputType.CheckBox && typeof formModel[requiredParam.name] === 'string') {
        formModel[requiredParam.name] = formModel[requiredParam.name].toLowerCase() === 'true';
      }
    });
    formMetadata.optionalParameters.forEach(optionalParam => {
      if (optionalParam.userInputType === InputType.CheckBox && typeof formModel[optionalParam.name] === 'string') {
        formModel[optionalParam.name] = formModel[optionalParam.name].toLowerCase() === 'true';
      }
    });

    if (Array.isArray(formModel.optionalFeatures)) {
      let optionalFeaturesFormArray: FormArray = systemForm.get('optionalFeatures') as FormArray;
      optionalFeaturesFormArray.clear();

      formModel.optionalFeatures.forEach(optionalFeature => {
        if (!formMetadata.optionalFeatures) {
          console.warn(`[SystemFormService] Model has optional feature '${optionalFeature?.name}' but metadata has none defined for '${formMetadata.name}'. Skipping.`);
          return;
        }
        if (this.isDeploymentScopedFeature(formMetadata, optionalFeature?.name)) {
          console.debug(`[SystemFormService] Feature '${optionalFeature.name}' is deployment-scoped for '${formMetadata.name}'. Skipping system-level binding.`);
          return;
        }
        let optionalFeatureMetadata = formMetadata.optionalFeatures.find(feature => feature.name === optionalFeature.name);
        if (!optionalFeatureMetadata) {
          console.warn(`[SystemFormService] No metadata found for optional feature '${optionalFeature?.name}' under parent '${formMetadata.name}'. Skipping.`);
          return;
        }
        // Skip features whose LaunchDarkly flag evaluates to false 
        if (!this.isFeatureEnabled(optionalFeatureMetadata)) {
          console.debug(`[SystemFormService] Feature '${optionalFeature.name}' is gated by flag '${optionalFeatureMetadata.featureFlag}' and currently disabled. Skipping.`);
          return;
        }
        optionalFeaturesFormArray.push(this.bindSystemForm(optionalFeatureMetadata, optionalFeature));
      });
    }

    systemForm.patchValue(formModel);

    return systemForm;
  }

  /**
   * Creates a dynamic form control from property source metadata.
   * @param source The property metadata.
   * @param required Whether the control is required on the form (passed in separately since this is not stored in the metadata).
   * @returns Angular FormControl constructed from the metadata.
   */
  createDynamicFormControl(source: UserInputPropertySource, required: boolean): FormControl {
    const dynamicFormControl = new FormControl(source.defaultValue);
    const validatorsToAdd = [];

    if (required) {            
      validatorsToAdd.push(Validators.required);
    }

    if (source.clientSideValidators) {      
      // Loop through the source.validators array and dynamically add validators
      source.clientSideValidators.forEach((validatorName) => {
        switch (validatorName) {      
          case ClientSideValidator.IPAddressValidation:
            validatorsToAdd.push(this.ipAddressValidator());
            break;
          // Add more cases as needed for other validators
          default:
            break;
        }
      });
    }
    dynamicFormControl.addValidators(validatorsToAdd);

    return dynamicFormControl;
  }
  

// IP address ValidatorFn
ipAddressValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Regular Expression for validating IPv4
    const ipv4Pattern = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

    // Regular Expression for validating IPv6
    const ipv6Pattern = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    
    // Check if the value is a valid IPv4 or IPv6 address
    if (!value) {
      return null; // Don't validate empty values
    }
    
    const isValidIpv4 = ipv4Pattern.test(value);
    const isValidIpv6 = ipv6Pattern.test(value);
    
    // Return an error if neither IPv4 nor IPv6 pattern matches
    return isValidIpv4 || isValidIpv6 ? null : { invalidIpAddress: true };
  };
}

  /**
   * Creates a map object used to track all unique deployment-level properties available in the deployments with relevant metadata. 
   * @param formMetadata Collection of deployment metadata.
   * @returns Map object that contains each deployment-level property as a key, along with the property metadata, 
   * list of features that reference it, and whether it is required on the form.
   * Example: 
   * {
   *   plxApiKey: {
   *     propMetadata: { 
   *       name: "plxApiKey",
   *       userInputType: ,
   *       ...
   *     },
   *     featureReferences: ["PLX"],
   *     required: false
   *   },
   *   plxEncryptionKey: etc...
   * }
   */
  getDeploymentLevelPropertyMap(formMetadata: DeploymentTypeMetadata[]): Map<string, { propMetadata: UserInputPropertySource, featureReferences: Set<string>, required: boolean }> {
    const uniqueProperties = new Map<string, { propMetadata: UserInputPropertySource, featureReferences: Set<string>, required: boolean }>();

    function aggregateFeatureLevelControls(metadata: DeploymentTypeMetadata[]) {
      metadata.forEach(metadatum => {
        const updatePropertyCollection = (source: UserInputPropertySource, required: boolean) => {
          if (!uniqueProperties.has(source.name)) {
            uniqueProperties.set(source.name, {
              propMetadata: source,
              featureReferences: new Set([metadatum.name, ...source.referencedByFeatures]),
              required: required
            });
            return;
          }

          let uniqueProperty = uniqueProperties.get(source.name);
          uniqueProperty.featureReferences = new Set([...Array.from(uniqueProperty.featureReferences), metadatum.name, ...source.referencedByFeatures]);
        };

        (metadatum.deploymentLevelRequiredParameters || []).forEach(param => updatePropertyCollection(param, true));
        (metadatum.deploymentLevelOptionalParameters || []).forEach(param => updatePropertyCollection(param, false));

        if (metadatum.optionalFeatures) {
          aggregateFeatureLevelControls(metadatum.optionalFeatures);
        }

        if (metadatum.deploymentFeatures) {
          aggregateFeatureLevelControls(metadatum.deploymentFeatures);
        }
      });
    }

    aggregateFeatureLevelControls(formMetadata);

    return uniqueProperties;
  }

  /**
   * Inspects the `referencedByFeatures` metadata property, and copies data recursively from parent features to any child features that reference it.
   * @param formMetadata The deployment metadata associated with the form model.
   * @param formModel The form model to transform.
   */
  setFeatureReferences(formMetadata: DeploymentTypeMetadata, formModel: any): void {
    // Iterate through all params in the feature
    formMetadata.requiredParameters.forEach(param => this.setParameterReferences(param, formModel[param.name], formModel));
    formMetadata.optionalParameters.forEach(param => this.setParameterReferences(param, formModel[param.name], formModel));

    // Do it again recursively for each subfeature, if it is enabled on the form
    formMetadata.optionalFeatures.forEach(optionalFeatureMetadata => {
      let optionalFeatureModel = formModel.optionalFeatures.find(model => model.name === optionalFeatureMetadata.name);

      if (optionalFeatureModel) {
        this.setFeatureReferences(optionalFeatureMetadata, optionalFeatureModel);
      }
    });
  }

  private setParameterReferences(param: UserInputPropertySource, value: any, formModel: any) {
    if (!param.referencedByFeatures || param.referencedByFeatures.length === 0) {
      return;
    }

    if (value === undefined) {
      console.warn(`Parameter '${param.name}' is referenced by other features but not present on the form.`);
      return;
    }

    formModel.optionalFeatures.forEach(optionalFeature => {
      if (param.referencedByFeatures.includes(optionalFeature.name)) {
        optionalFeature[param.name] = value;
      }

      optionalFeature.optionalFeatures.forEach(subFeature => this.setParameterReferences(param, value, subFeature));
    });
  }

  private isDeploymentScopedFeature(formMetadata: DeploymentTypeMetadata, featureName: string): boolean {
    return (formMetadata.deploymentFeatures || []).some(feature => feature.name === featureName);
  }
}
